import React, { useEffect, useState } from "react";
import MainLayout from "../../layouts/MainLayout";
import DataTable from "../../components/DataTable";
import Alert from "../../components/Alert";
import { listClassConfigurations } from "../../api/classConfigurationApi";
import { listOthersHolidays, createOthersHoliday, deleteOthersHoliday } from "../../api/attendanceApi";
import "../Users/UsersPage.css";
import "./Holiday.css";

function daysBetween(fromDate, toDate) {
  const dates = [];
  const cursor = new Date(fromDate + "T00:00:00");
  const end = new Date(toDate + "T00:00:00");
  while (cursor <= end) {
    dates.push(`${cursor.getFullYear()}-${String(cursor.getMonth() + 1).padStart(2, "0")}-${String(cursor.getDate()).padStart(2, "0")}`);
    cursor.setDate(cursor.getDate() + 1);
  }
  return dates;
}

/**
 * Direct port of digicampusbd's `others-holiday.html` /
 * `OthersHolidayService.othersHolidayClassAssignmentSave`. Found while
 * sweeping the Holiday module (see MIGRATION.md): the real form lets staff
 * queue multiple date ranges (a jQuery `daterangepicker`) before one Save,
 * which flattens to a `dateList` sent in one request — the real backend
 * creates one `OthersHoliday` row per date in that list. This migration's
 * form only ever took a single arbitrary date. Added a From/To Date range
 * (a single range, not the old app's own multi-range queue UI — same real
 * capability of creating several days' holidays in one action, via the
 * already-established client-side day-expansion pattern used for
 * `LeaveManager`) that loops the already-correct single-day
 * `createOthersHoliday` endpoint once per real calendar day in the range —
 * no backend change needed, since the per-day primitive was already right.
 */
export default function OthersHolidayPage() {
  const [holidays, setHolidays] = useState([]);
  const [classConfigurations, setClassConfigurations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [form, setForm] = useState({ fromDate: "", toDate: "", description: "", shortName: "", classConfigurationIds: [] });

  const load = () => {
    setLoading(true);
    Promise.all([listOthersHolidays(), listClassConfigurations()])
      .then(([holidayList, classList]) => {
        setHolidays(holidayList);
        setClassConfigurations(classList);
      })
      .catch(() => setError("Failed to load holidays."))
      .finally(() => setLoading(false));
  };

  useEffect(load, []);

  const toggleClass = (id) => {
    setForm((f) => ({
      ...f,
      classConfigurationIds: f.classConfigurationIds.includes(id)
        ? f.classConfigurationIds.filter((c) => c !== id)
        : [...f.classConfigurationIds, id],
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (!form.fromDate || !form.toDate) {
      setError("From Date and To Date are required.");
      return;
    }
    if (form.toDate < form.fromDate) {
      setError("To Date can't be before From Date.");
      return;
    }
    const dates = daysBetween(form.fromDate, form.toDate);
    setSaving(true);
    try {
      // One real OthersHoliday row per real calendar day — direct port of the old
      // app's own server-side dateList expansion, done here against the same
      // single-day endpoint.
      for (const date of dates) {
        // eslint-disable-next-line no-await-in-loop
        await createOthersHoliday({
          date,
          description: form.description,
          shortName: form.shortName,
          classConfigurationIds: form.classConfigurationIds,
        });
      }
      setSuccess(`Holiday created for ${dates.length} day(s).`);
      setForm({ fromDate: "", toDate: "", description: "", shortName: "", classConfigurationIds: [] });
      load();
    } catch (err) {
      setError((err.response && err.response.data && err.response.data.message) || "Failed to create.");
      load(); // some days in the range may have already saved before the failing one
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (row) => {
    if (!window.confirm(`Delete holiday "${row.description}"?`)) return;
    setError("");
    try {
      await deleteOthersHoliday(row.id);
      setSuccess("Deleted.");
      setHolidays((prev) => prev.filter((h) => h.id !== row.id));
    } catch (err) {
      setError((err.response && err.response.data && err.response.data.message) || "Failed to delete.");
    }
  };

  const columns = [
    { key: "date", label: "Date" },
    { key: "description", label: "Description" },
    { key: "shortName", label: "Short Name" },
    { key: "classConfigNames", label: "Classes", render: (row) => row.classConfigNames.join(", ") },
  ];

  return (
    <MainLayout>
      <div className="page-header">
        <h1>Other Holidays</h1>
      </div>

      <Alert type="error" onDismiss={() => setError("")}>
        {error}
      </Alert>
      <Alert type="success" onDismiss={() => setSuccess("")}>
        {success}
      </Alert>

      <form onSubmit={handleSubmit} className="user-form" style={{ marginBottom: 24 }}>
        <label>From Date *</label>
        <input type="date" value={form.fromDate} onChange={(e) => setForm({ ...form, fromDate: e.target.value })} required />
        <label>To Date *</label>
        <input type="date" value={form.toDate} onChange={(e) => setForm({ ...form, toDate: e.target.value })} required />
        <label>Description</label>
        <input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="e.g. Language Martyrs' Day" />
        <label>Short Name</label>
        <input value={form.shortName} onChange={(e) => setForm({ ...form, shortName: e.target.value })} placeholder="e.g. 21 Feb" />
        <label>Classes *</label>
        <div className="checkbox-grid">
          {classConfigurations.map((c) => (
            <label key={c.id}>
              <input
                type="checkbox"
                checked={form.classConfigurationIds.includes(c.id)}
                onChange={() => toggleClass(c.id)}
              />
              <span>{c.classConfigName}</span>
            </label>
          ))}
        </div>
        <div className="form-actions">
          <button type="submit" className="btn-primary" disabled={saving || form.classConfigurationIds.length === 0}>
            {saving ? "Saving..." : "+ Add Holiday"}
          </button>
        </div>
      </form>

      {loading ? (
        <p>Loading...</p>
      ) : (
        <DataTable
          columns={columns}
          rows={holidays}
          actions={(row) => (
            <button type="button" className="btn-link danger" onClick={() => handleDelete(row)}>
              Delete
            </button>
          )}
        />
      )}
    </MainLayout>
  );
}
